import type { DownloadFile, Game, Profile } from "@/domain/types";
import type { SC2Source } from "@/games/sc2/source";
import { type CatalogEntry, catalogEntries } from "@/platform/thecore/ingest";
import { resolveSc2Sources, UPLOADED_ID } from "./sc2-sources";

export { UPLOADED_ID };

// The per-Game software CHOOSING, resolved in one place. SC2 selects among text sources (catalog
// Presets, stock tables, the Edennil setup, an upload); every other Game selects among its
// Profiles (plus an uploaded "my own" one). The page previously computed each consequence of that
// choice - the pin, the published Preset, the file download, the lens name-key - as its own
// `sc2 ?` branch; this seam yields them all on one discriminated value, so the two models cannot
// drift apart and an illegal mix (an SC2 view holding a simple-Game Profile) is unrepresentable.
// Rendering stays one generic projector for every Game - the seam concentrates choosing only,
// never the resolution path.

/** A board-bound setup's pinned lens (the Profile/SC2Source `pin` shape). */
export type LensPin = { keyboard: string; layer: string };

/**
 * The resolved active setup for one render side. `pin`, `preset`, `download` and `nameKey` are the
 * model-independent yields the page consumes without branching; the arm-specific fields feed the
 * Profile picker (its option list) and useSide (the source text / the Profile).
 */
export type ActiveSetup =
  | {
      kind: "sc2";
      /** The Profile picker's source list (catalog + stock + personal + upload splice). */
      sources: SC2Source[];
      /** The active source; a stale id has already derived back to the landing default. */
      source: SC2Source | null;
      pin: LensPin | null;
      /** The active source's published Preset (locale downloads, share route); null under a pin. */
      preset: CatalogEntry | null;
      /** SC2 downloads ride the Preset catalog, never a per-Profile file. */
      download: null;
      /** The lens-identity name part (readout reset key), not display copy. */
      nameKey: string;
    }
  | {
      kind: "profile";
      /** The Profile picker's options (the Game's built-ins + the uploaded "my own" Profile). */
      profiles: Profile[];
      /** The active Profile; a stale id has already derived back to the landing default. */
      profile: Profile | null;
      pin: LensPin | null;
      /** A simple Game has no Preset catalog. */
      preset: null;
      /** The Profile's vendored source file, downloadable verbatim; layout-independent, so a
       * board pin does not suppress it (unlike the Preset). */
      download: DownloadFile | null;
      /** The lens-identity name part (readout reset key), not display copy. */
      nameKey: string;
    };

export interface ActiveSetupInput {
  game: Game;
  /** The persisted SC2 source selection. */
  sc2SourceId: string;
  /** The session's uploaded .SC2Hotkeys, offered as its own source. */
  sc2Upload: { text: string; name: string } | null;
  /** The persisted simple-Game Profile selection for this Game. */
  selectedProfileId: string | undefined;
  /** The uploaded "my own" Profile, already ingested (useActiveSetup memoizes the parse). */
  uploadedProfile: Profile | null;
  /** Compare B: drop board-bound (pinned) sources - a pinned setup must never render on an
   * unpinned board, and an independent pinned B side is deferred. */
  portableOnly?: boolean;
}

/** Resolve which software is active for one side, and every consequence the page consumes. */
export function resolveActiveSetup({
  game,
  sc2SourceId,
  sc2Upload,
  selectedProfileId,
  uploadedProfile,
  portableOnly = false,
}: ActiveSetupInput): ActiveSetup {
  if (game.id === "sc2") {
    const { sources, activeSource } = resolveSc2Sources(sc2Upload, sc2SourceId, portableOnly);
    const pin = activeSource?.pin ?? null;
    return {
      kind: "sc2",
      sources,
      source: activeSource,
      pin,
      // the published Preset is suppressed while a pin holds: a board-bound setup has no locale
      // switch/download (and none of the pinned sources is a catalog Preset today anyway)
      preset:
        !pin && activeSource
          ? (catalogEntries.find((e) => e.id === activeSource.id) ?? null)
          : null,
      download: null,
      nameKey: activeSource?.name ?? game.id,
    };
  }
  const profiles = uploadedProfile ? [...game.profiles, uploadedProfile] : game.profiles;
  const profile = profiles.find((p) => p.id === selectedProfileId) ?? profiles[0] ?? null;
  return {
    kind: "profile",
    profiles,
    profile,
    pin: profile?.pin ?? null,
    preset: null,
    download: profile?.download ?? null,
    nameKey: `${game.id}:${profile?.id ?? ""}`,
  };
}
